import _ from "lodash";
import React, { useState, useEffect } from "react";
import classes from "./LinkifyModal.scss";
import I18nHOC from "UIComponents/I18nHOC";
import RadioButton from "UIComponents/RadioButton";
import { CreateDropDown } from "Attachment";
import { compose } from "lib/apolloClient";
import { connect } from "react-redux";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { isURL, getThumbUrl, htmlToText, addHttpsInUrl } from "Utils";
import { getWebLinkDetails } from "modules/Services";
import {
  TextInput,
  Button,
  MediaCard,
  IconButton,
  Modal,
} from "@toddle-edu/ds-web";
import { DeleteOutlined } from "@toddle-edu/ds-icons";

const DROP_DOWN_OPTIONS = [
  { key: "upload_device", label: "upload_device" },
  { key: "add_google_drive", label: "add_google_drive" },
  { key: "add_one_drive", label: "add_one_drive" },
];

const RADIO_BUTTONS = [
  { label: "common:add_url_uppercase", value: "URL" },
  { label: "common:upload_a_file", value: "FILE" },
];

const containerStyle = {
  display: "flex",
  flexDirection: "column",
  width: "100%",
};

const modalStyles = {
  modalBodyStyle: {
    padding: "8px 16px",
  },
  modalHeaderStyle: {
    padding: "12px 16px",
  },
  modalFooterStyle: {
    padding: "12px 16px",
  },
};

const buttonDropDownStyles = {
  buttonDropdownParentContainerStyle: {
    width: "100%",
  },
  buttonDropdownButtonComponentStyle: {
    height: "100%",
  },
  buttonDropDownContainerStyle: {
    top: "52px",
    width: "100%",
  },
};

const hoverContainerStyles = {
  width: "100%",
};

const inputRefs = {};

const getRadioButtons = ({
  t,
  onClickRadioButton,
  selectedButton,
  isLoading,
  testId,
}) => {
  return (
    <div className={classes.radioButtonContainer}>
      {_.map(RADIO_BUTTONS, (button, index) => {
        return (
          <div
            className={classes.radioContainer}
            key={button.value}
            onClick={() => onClickRadioButton(button.value)}
          >
            <RadioButton
              dsVersion="2.0"
              label={t(button.label)}
              isChecked={selectedButton === button.value}
              color={"blue"}
              isDisabled={isLoading}
              testId={testId}
              size="medium"
            ></RadioButton>
          </div>
        );
      })}
    </div>
  );
};

const LinkifyModal = ({
  url: urlFromProp,
  title: titleFromProp = "",
  linkType: linkTypeFromProp,
  filename: filenameFromProp,
  mimeType: mimeTypeFromProp,
  fileType: fileTypeFromProp,
  onClickSave,
  onClose,
  t,
  setToastMsg,
  testId,
  customFilterFilesHandler,
  getWebLinkDetails,
  mode = "create",
}) => {
  // states
  const [urlTypeState, setUrlTypeState] = useState({});
  const [fileTypeState, setFileTypeState] = useState({});
  const [linkType, setLinkType] = useState("URL");
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [inputFieldErrors, setInputFieldErrors] = useState({
    url: "",
    title: "",
  });

  // vars
  let state = {};
  if (linkType === "URL") {
    state = urlTypeState;
  }
  if (linkType === "FILE") {
    state = fileTypeState;
  }
  const { filename, url, mimeType, fileType } = state;

  useEffect(() => {
    const lType = linkTypeFromProp || "URL";
    if (lType === "URL") {
      setUrlTypeState({
        url: urlFromProp,
      });
    } else if (lType === "FILE") {
      setFileTypeState({
        url: urlFromProp,
        filename: filenameFromProp,
        mimeType: mimeTypeFromProp,
        fileType: fileTypeFromProp,
      });
    }
    setLinkType(lType);
  }, []);

  const updateUrlTypeState = params => {
    setUrlTypeState(prevState => {
      return {
        ...prevState,
        ...params,
      };
    });
  };

  const updateFileTypeState = params => {
    setFileTypeState(prevState => {
      return {
        ...prevState,
        ...params,
      };
    });
  };

  const updateState = params => {
    switch (linkType) {
      case "URL": {
        updateUrlTypeState(params);
        break;
      }
      case "FILE": {
        updateFileTypeState(params);
        break;
      }
      default: {
        break;
      }
    }
  };

  const getURLAttachmentDetails = async () => {
    const url = _.trim(state.url);
    if (isURL(url)) {
      const linkUrl = addHttpsInUrl(url);
      const urlDetailObject = await getWebLinkDetails(linkUrl);

      if (urlDetailObject) {
        return _.first(urlDetailObject.attachments);
      }
    }

    return null;
  };

  const onSave = async () => {
    if (validateInputFields()) {
      let errors = 0;
      _.forEach(inputRefs, ref => {
        if (ref && !ref.value) {
          errors++;
        }
      });
      // validate if there is no file uploaded
      if (!_.trim(url)) {
        errors += 1;
        setError(t("common:please_upload_a_file"));
      }
      if (errors > 0) {
        setToastMsg({
          msg: "toastMsgs:please_fill_required_fields",
          type: "warning",
          position: "toast-top-center",
        });
        return;
      }

      // When showDisplayTextInput is false, we don't allow user to enter display text
      // so fetching URL details to get title, mimeType etc
      let urlAttachmentDetails;
      setLoading(true);
      if (linkType === "URL") {
        urlAttachmentDetails = await getURLAttachmentDetails();
      }

      const params = {
        ...state,
        linkType,
        title:
          linkType === "URL" ? urlAttachmentDetails?.name : state?.filename,
        ...urlAttachmentDetails,
      };
      if (onClickSave) {
        await onClickSave(params);
      }
      setLoading(false);
    }
  };

  const onClickRadioButton = value => {
    setLinkType(value);
  };

  const onAddAttachment = params => {
    const attachment = _.get(params, "0", {});
    const { url, mimeType, name: filename, type } = attachment;
    updateState({ url, filename, mimeType, fileType: type });
    setError("");
  };

  const { thumbUrl, isIcon } = getThumbUrl({
    attachment: { ...state, type: state.fileType },
    width: 40,
    height: 40,
  });

  const validateInputFields = () => {
    if (!isURL(state?.url)) {
      setInputFieldErrors({
        title: "",
        url: t("common:web_link_not_valid"),
      });
      return false;
    } else {
      setInputFieldErrors({
        url: "",
        title: "",
      });
      return true;
    }
  };

  const getUrlContainer = () => {
    switch (linkType) {
      case "URL": {
        return (
          <div style={containerStyle}>
            <TextInput
              dsVersion="2.0"
              value={url}
              name={"url"}
              onChange={e => updateState({ url: e.target.value })}
              label={t("common:url")}
              isRequired={true}
              ref={ref => (inputRefs["url"] = ref)}
              placeholder={t("common:enter_with_label", {
                label: t("common:url"),
              })}
              error={inputFieldErrors.url}
              size={"medium"}
              testId={`${testId}:url`}
              disabled={isLoading}
            />
            {inputFieldErrors.url && (
              <div className={classes.errorText}>{inputFieldErrors.url}</div>
            )}
          </div>
        );
      }
      case "FILE": {
        const isUploaded = url && mimeType;
        // eslint-disable-next-line no-restricted-properties
        const windowOpen = window.open;
        return isUploaded ? (
          <div className={classes.uploadButtonContainer}>
            <MediaCard
              dsVersion="2.0"
              cardType="medium"
              name={htmlToText(filename) || t("common:untitled")}
              type={fileType}
              mimeType={mimeType}
              url={url}
              thumbUrl={isIcon ? undefined : thumbUrl}
              showActionsOnHover
              onClick={() => windowOpen && windowOpen(url, "_blank").focus()}
              options={
                <IconButton
                  dsVersion="2.0"
                  onClick={() => updateState({ url: "" })}
                  icon={<DeleteOutlined variant="subtle" size="xx-small" />}
                  variant="neutral"
                  shouldStopPropagation
                  disabled={isLoading}
                  testId={`${testId}:delete`}
                />
              }
              testId={testId}
            />
          </div>
        ) : (
          <div className={classes.uploadButtonContainer}>
            <CreateDropDown
              dropDownOptions={DROP_DOWN_OPTIONS}
              buttonComponent={
                <Button
                  dsVersion="2.0"
                  size="medium"
                  variant="neutral"
                  type="outlined"
                  disabled={isLoading}
                  isFullWidth
                  testId={`${testId}:create-dropdown`}
                >
                  {t("common:upload_with_label", {
                    label: t("common:lowercase", {
                      text: t("common:file"),
                    }),
                  })}
                </Button>
              }
              updateLoadingState={setLoading}
              addAttachment={onAddAttachment}
              {...buttonDropDownStyles}
              multiselect={false}
              hoverContainerStyles={hoverContainerStyles}
              customFilterFilesHandler={customFilterFilesHandler}
              isNewDropdown={true}
              dsVersion={"2.0"}
            />
            {error && <div className={classes.uploadError}>{error}</div>}
          </div>
        );
      }
    }
  };

  const headerTitle =
    mode === "create"
      ? t("common:insert_with_label", {
          label: t("common:lowercase", { text: t("common:link") }),
        })
      : t("common:edit_link");

  return (
    <Modal dsVersion="2.0" onClose={onClose} width="410px" height="426px">
      <Modal.Header
        isClosable={true}
        hasSeparator={false}
        title={headerTitle}
      />

      <Modal.Body style={modalStyles.modalBodyStyle}>
        <div className={classes.modalBodyContainer}>
          {getRadioButtons({
            t,
            onClickRadioButton,
            selectedButton: linkType,
            isLoading,
            testId,
          })}
          <div className={classes.inputsContainer}>{getUrlContainer()}</div>
        </div>
      </Modal.Body>

      <Modal.Footer style={modalStyles.modalFooterStyle}>
        <footer className={classes.footerContainer}>
          <div className={classes.loaderContainer}>
            {isLoading ? (
              <div className={classes.analysisLoaderContainer}>
                <div className={classes.dot} />
                <div className="text-label-3">
                  {t("common:processing_no_dots")}
                </div>
              </div>
            ) : null}
          </div>
          <div className={classes.buttonsContainer}>
            <Button
              dsVersion="2.0"
              size="medium"
              onClick={onClose}
              type="outlined"
              variant="neutral"
              testId={`${testId}:cancel`}
            >
              {t("common:cancel")}
            </Button>
            <Button
              dsVersion="2.0"
              size="medium"
              disabled={isLoading || !url}
              onClick={onSave}
              variant={mode === "edit" ? "destructive" : "progressive"}
              testId={`${testId}:save`}
            >
              {mode === "edit"
                ? t("common:update")
                : t("equationEditor:insert")}
            </Button>
          </div>
        </footer>
      </Modal.Footer>
    </Modal>
  );
};

const mapActionCreators = {
  setToastMsg,
  getWebLinkDetails,
};

export default compose(
  I18nHOC({ resource: ["common"] }),
  connect(null, mapActionCreators)
)(LinkifyModal);
